<template>
  <Transition
    enter-active-class="toast-enter-active"
    enter-from-class="toast-enter-from"
    enter-to-class="toast-enter-to"
    leave-active-class="toast-leave-active"
    leave-from-class="toast-leave-from"
    leave-to-class="toast-leave-to"
  >
    <div v-if="show" class="toast-container">
      <div 
        class="toast-content"
        :class="{
          'toast-success': type === 'success',
          'toast-error': type === 'error',
          'toast-info': type === 'info',
          'toast-warning': type === 'warning'
        }"
      >
        <!-- Icon -->
        <div class="toast-icon">
          <CheckCircle v-if="type === 'success'" class="icon success" />
          <XCircle v-if="type === 'error'" class="icon error" />
          <Info v-if="type === 'info'" class="icon info" />
          <AlertTriangle v-if="type === 'warning'" class="icon warning" />
        </div>

        <!-- Content -->
        <div class="toast-message">
          <h3 v-if="title" class="toast-title">{{ title }}</h3>
          <p class="toast-text">{{ message }}</p>
        </div>

        <!-- Close Button -->
        <button 
          @click="closeToast"
          class="toast-close"
        >
          <X class="icon" />
        </button>
      </div>
    </div>
  </Transition>
</template>
  
  <script setup>
  import { ref, onMounted } from 'vue'
  import { CheckCircle, XCircle, Info, AlertTriangle, X } from 'lucide-vue-next'
  
  const props = defineProps({
    title: {
      type: String,
      default: ''
    },
    message: {
      type: String,
      required: true
    },
    type: {
      type: String,
      default: 'info',
      validator: (value) => ['success', 'error', 'info', 'warning'].includes(value)
    },
    duration: {
      type: Number,
      default: 3000
    },
    autoClose: {
      type: Boolean,
      default: true
    }
  })
  
  const emit = defineEmits(['close'])
  const show = ref(true)
  
  const closeToast = () => {
    show.value = false
    emit('close')
  }
  
  onMounted(() => {
    if (props.autoClose && props.duration > 0) {
      setTimeout(closeToast, props.duration)
    }
  })
  </script>
<style scoped>
.toast-container {
  position: fixed;
  top: calc(60px + 1rem); /* 导航栏高度 + 上边距 */
  right: 1rem;
  z-index: 50; /* 确保比导航栏小，但比其他内容大 */
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.toast-content {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 320px;
  padding: 1rem;
  background-color: #1E1E2E;
  border: 1px solid rgba(139, 92, 246, 0.2);
  border-radius: 0.5rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.toast-icon {
  flex-shrink: 0;
}

.icon {
  width: 1.25rem;
  height: 1.25rem;
}

.toast-message {
  flex: 1;
}

.toast-title {
  margin-bottom: 0.25rem;
  font-weight: 500;
  color: #F3F4F6;
}

.toast-text {
  font-size: 0.875rem;
  color: #D1D5DB;
}

.toast-close {
  flex-shrink: 0;
  color: #9CA3AF;
  transition: color 0.2s;
}

.toast-close:hover {
  color: #F3F4F6;
}

/* 类型样式 */
.toast-success {
  border-left: 4px solid #10B981;
}

.toast-error {
  border-left: 4px solid #EF4444;
}

.toast-info {
  border-left: 4px solid #3B82F6;
}

.toast-warning {
  border-left: 4px solid #F59E0B;
}

/* 图标颜色 */
.icon.success {
  color: #10B981;
}

.icon.error {
  color: #EF4444;
}

.icon.info {
  color: #3B82F6;
}

.icon.warning {
  color: #F59E0B;
}

/* 动画样式 */
.toast-enter-active {
  transition: all 0.3s ease-out;
}

.toast-leave-active {
  transition: all 0.2s ease-in;
}

.toast-enter-from,
.toast-leave-to {
  transform: translateY(0.5rem);
  opacity: 0;
}

.toast-enter-to,
.toast-leave-from {
  transform: translateY(0);
  opacity: 1;
}
</style>